import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";

import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { FolderIcon, folderColorClasses } from "@/components/folderTheme";
import AddToFolderDropdown from "@/components/AddToFolderDropdown";

/**
 * Compact row of folder chips shown under the run-detail header.
 * Clicking a chip navigates to /folders/{folder_id}.
 * The "+" trigger opens the AddToFolderDropdown.
 */
export default function RunFolderChips({ runId }) {
  const membershipQuery = useQuery({
    queryKey: ["run-folders", runId],
    queryFn: async () => (await api.get(`/demo-runs/${runId}/folders`)).data,
    enabled: !!runId,
  });

  const folders = membershipQuery.data?.folders || [];

  return (
    <div
      className="flex flex-wrap items-center gap-1.5"
      data-testid="run-folder-chips"
    >
      {folders.map((f) => {
        const p = folderColorClasses(f.color);
        return (
          <Link
            key={f.folder_id}
            to={`/folders/${f.folder_id}`}
            className={[
              "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium transition hover:shadow-sm",
              p.bg,
              p.text,
              p.border,
            ].join(" ")}
            data-testid={`run-folder-chip-${f.folder_id}`}
          >
            <FolderIcon name={f.icon} size={11} />
            {f.name}
          </Link>
        );
      })}
      <AddToFolderDropdown runId={runId}>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-2 text-[11px] text-slate-600 hover:bg-indigo-50 hover:text-indigo-700"
          data-testid="run-folder-chips-add"
        >
          <Plus size={12} />
          {folders.length === 0 ? "Add to folder" : "Add"}
        </Button>
      </AddToFolderDropdown>
    </div>
  );
}
